import { useLayoutEffect, useRef, type RefObject } from 'react';
import { SIDEBAR_FOLD_EASE, SIDEBAR_FOLD_MS } from '../context/SidebarContext';

/**
 * Moves something pinned beside the left sidebar (the main area, the CRM frame)
 * in step with the sidebar when either of its columns opens or closes.
 *
 * Its offset (`sidebarWidth`, inline) still snaps to the new value in one layout pass:
 * animating the margin re-laid out the whole page, and resized the CRM iframe,
 * on every frame. But a bare snap made the page jump at once while the sidebar
 * took its 260ms to follow, which read as the sidebar lagging. So right after
 * the snap (before paint) the element is drawn back where it was and slid home
 * with `translate`, on the sidebar's own duration and easing, so the two edges
 * move together. A translate runs on the compositor: no layout or paint per frame.
 *
 * While it slides the element carries `is-sliding`: a translate makes it the
 * containing block of its fixed children, which then pin to its edge instead of
 * offsetting by the sidebar (CrmPageRail.css, CrmLoggedOutBanner.css).
 *
 * `width` is the sidebar's width in px; `enabled` is false where the sidebar is a
 * phone drawer (crossing that breakpoint must not slide anything).
 */
export function useFoldSlide(ref: RefObject<HTMLElement | null>, width: number, enabled: boolean) {
  const prev = useRef({ width, enabled });
  const slide = useRef<Animation | null>(null);

  useLayoutEffect(() => {
    const was = prev.current;
    prev.current = { width, enabled };
    const el = ref.current;
    if (!el || !enabled || !was.enabled || was.width === width) return;
    if (typeof el.animate !== 'function' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const step = was.width - width;
    // Start from where it is drawn now: a re-toggle mid-slide continues from there.
    const drawn = slide.current ? parseFloat(getComputedStyle(el).translate) || 0 : 0;
    slide.current?.cancel();
    const from = step + drawn;
    if (Math.abs(from) < 0.5) {
      el.classList.remove('is-sliding');
      return;
    }
    // A transition reversed part way is shortened in proportion, as CSS does.
    const duration = SIDEBAR_FOLD_MS * Math.min(1, Math.abs(from) / Math.abs(step));
    el.classList.add('is-sliding');
    const animation = el.animate([{ translate: `${from}px 0` }, { translate: '0 0' }], { duration, easing: SIDEBAR_FOLD_EASE });
    slide.current = animation;
    animation.onfinish = () => {
      if (slide.current !== animation) return;
      slide.current = null;
      el.classList.remove('is-sliding');
    };
  }, [ref, width, enabled]);
}
